import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SeoHead, { BASE_URL } from "@/components/feature/SeoHead";

type Step = 1 | 2 | 3;

interface ProductEntry {
  id: string;
  name: string;
  category: string;
  samplePrice: string;
  bulkPrice: string;
  moq: string;
  dropshipping: boolean;
  description: string;
}

const steps = [
  { num: 1, label: "Company Info", icon: "ri-building-4-line" },
  { num: 2, label: "Products & Pricing", icon: "ri-price-tag-3-line" },
  { num: 3, label: "Verification", icon: "ri-shield-check-line" },
];

const emptyProduct = (): ProductEntry => ({
  id: Math.random().toString(36).slice(2),
  name: "",
  category: "",
  samplePrice: "",
  bulkPrice: "",
  moq: "",
  dropshipping: false,
  description: "",
});

const RegisterSupplierPage = () => {
  const [step, setStep] = useState<Step>(1);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Step 1 fields
  const [company, setCompany] = useState({
    name: "", country: "", city: "", address: "", website: "", contactName: "",
    email: "", phone: "", employees: "", established: "", businessType: "", description: "",
  });

  // Step 2 fields
  const [products, setProductList] = useState<ProductEntry[]>([emptyProduct()]);
  const [dropshippingAvailable, setDropshippingAvailable] = useState(false);
  const [warehouseCountries, setWarehouseCountries] = useState("");

  // Step 3 fields
  const [verification, setVerification] = useState({
    businessLicense: false, iso: false, ce: false, halal: false, other: "",
    agreeTerms: false,
  });

  const handleCompanyChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setCompany((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleProductChange = (id: string, field: keyof ProductEntry, value: string | boolean) => {
    setProductList((prev) => prev.map((p) => p.id === id ? { ...p, [field]: value } : p));
  };

  const addProduct = () => setProductList((prev) => [...prev, emptyProduct()]);
  const removeProduct = (id: string) => setProductList((prev) => prev.filter((p) => p.id !== id));

  const handleSubmit = async () => {
    if (!verification.agreeTerms) return;
    setSubmitting(true);
    try {
      const body = new URLSearchParams();
      // Company info
      Object.entries(company).forEach(([k, v]) => body.append(k, v));
      body.append("dropshippingAvailable", String(dropshippingAvailable));
      body.append("warehouseCountries", warehouseCountries);
      body.append("products", JSON.stringify(products));
      body.append("certifications", [
        verification.businessLicense && "Business License",
        verification.iso && "ISO Certified",
        verification.ce && "CE Certified",
        verification.halal && "Halal Certified",
        verification.other,
      ].filter(Boolean).join(", "));

      await fetch("https://readdy.ai/api/form/d7ek3ijfimgqccpmabs0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-amber-50">
        <Navbar />
        <div className="pt-32 pb-20 flex items-center justify-center px-4">
          <div className="bg-white rounded-3xl p-12 max-w-lg w-full text-center border border-amber-100">
            <div className="w-20 h-20 flex items-center justify-center bg-amber-100 rounded-full mx-auto mb-6">
              <i className="ri-checkbox-circle-fill text-4xl text-amber-500"></i>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
              Registration Submitted!
            </h2>
            <p className="text-gray-500 text-sm mb-6 leading-relaxed">
              Thank you for registering as a supplier on SuperMiddleEast. Our verification team will review your application within <strong>2–3 business days</strong> and contact you via email.
            </p>
            <div className="bg-amber-50 rounded-xl p-4 mb-6 text-left space-y-2">
              <div className="flex items-center gap-2 text-sm text-gray-700"><i className="ri-mail-check-line text-amber-500"></i> Confirmation email sent to {company.email}</div>
              <div className="flex items-center gap-2 text-sm text-gray-700"><i className="ri-time-line text-amber-500"></i> Verification: 2–3 business days</div>
              <div className="flex items-center gap-2 text-sm text-gray-700"><i className="ri-shield-check-line text-amber-500"></i> Products go live after approval</div>
            </div>
            <a href="/" className="inline-block bg-amber-500 hover:bg-amber-600 text-white font-semibold px-8 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
              Back to Homepage
            </a>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-amber-50">
      <SeoHead
        title="Register as a Supplier – Join 5 Million Factories | SuperMiddleEast"
        description="Register as a verified supplier on SuperMiddleEast. List your factory products, set sample and bulk prices, enable dropshipping and reach global buyers from Dubai, Saudi Arabia and beyond."
        keywords="register supplier Middle East, factory supplier registration, verified supplier, list factory, sell wholesale Middle East"
        canonical={`${BASE_URL}/register-supplier`}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Register as a Supplier – SuperMiddleEast",
            "description": "Register as a verified supplier on SuperMiddleEast. List your factory products and reach global buyers.",
            "url": `${BASE_URL}/register-supplier`,
            "inLanguage": "en",
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home", "item": BASE_URL },
                { "@type": "ListItem", "position": 2, "name": "Register Supplier", "item": `${BASE_URL}/register-supplier` },
              ],
            },
          },
        ]}
      />
      <Navbar />

      {/* Hero */}
      <section className="bg-amber-900 pt-24 pb-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-amber-400 rounded-full -translate-y-1/2 translate-x-1/2"></div>
        </div>
        <div className="relative z-10 max-w-screen-xl mx-auto px-4 md:px-8 text-center">
          <p className="text-amber-400 text-xs font-semibold uppercase tracking-widest mb-3">Join 5 Million Factories</p>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Register as a Supplier
          </h1>
          <p className="text-amber-200 text-base max-w-xl mx-auto">
            List your factory, upload products with pricing, and reach global buyers — including dropshippers and influencers worldwide.
          </p>
        </div>
      </section>

      {/* Step Indicator */}
      <div className="bg-white border-b border-amber-100 sticky top-16 md:top-20 z-30">
        <div className="max-w-screen-xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-center gap-2 md:gap-8">
            {steps.map((s, i) => (
              <div key={s.num} className="flex items-center gap-2">
                <div className={`flex items-center gap-2 cursor-pointer ${step >= s.num ? "text-amber-600" : "text-gray-400"}`}
                  onClick={() => step > s.num && setStep(s.num as Step)}>
                  <div className={`w-8 h-8 flex items-center justify-center rounded-full text-sm font-bold transition-colors ${step > s.num ? "bg-amber-500 text-white" : step === s.num ? "bg-amber-500 text-white" : "bg-gray-100 text-gray-400"}`}>
                    {step > s.num ? <i className="ri-check-line"></i> : s.num}
                  </div>
                  <span className="hidden sm:block text-sm font-medium">{s.label}</span>
                </div>
                {i < steps.length - 1 && (
                  <div className={`w-8 md:w-16 h-0.5 mx-1 ${step > s.num ? "bg-amber-400" : "bg-gray-200"}`}></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 md:px-8 py-10">

        {/* ── STEP 1: Company Info ── */}
        {step === 1 && (
          <div className="bg-white rounded-2xl p-6 md:p-8 border border-amber-100">
            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <i className="ri-building-4-line text-amber-500"></i> Company Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="md:col-span-2">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Company / Factory Name *</label>
                <input name="name" value={company.name} onChange={handleCompanyChange} placeholder="e.g. Al Futtaim Industrial Group" className="w-full border border-amber-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-amber-400" required />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Country *</label>
                <select name="country" value={company.country} onChange={handleCompanyChange} className="w-full border border-amber-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-amber-400 bg-white cursor-pointer">
                  <option value="">Select country...</option>
                  <option>UAE</option><option>Saudi Arabia</option><option>Qatar</option>
                  <option>China</option><option>India</option><option>Turkey</option><option>Other</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">City *</label>
                <input name="city" value={company.city} onChange={handleCompanyChange} placeholder="e.g. Dubai" className="w-full border border-amber-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-amber-400" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Factory Address</label>
                <input name="address" value={company.address} onChange={handleCompanyChange} placeholder="Full address" className="w-full border border-amber-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-amber-400" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Website</label>
                <input name="website" value={company.website} onChange={handleCompanyChange} placeholder="https://yourfactory.com" className="w-full border border-amber-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-amber-400" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Business Type</label>
                <select name="businessType" value={company.businessType} onChange={handleCompanyChange} className="w-full border border-amber-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-amber-400 bg-white cursor-pointer">
                  <option value="">Select type...</option>
                  <option>Manufacturer</option><option>Trading Company</option>
                  <option>Manufacturer + Trading</option><option>Distributor</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Contact Person *</label>
                <input name="contactName" value={company.contactName} onChange={handleCompanyChange} placeholder="Full name" className="w-full border border-amber-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-amber-400" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Email Address *</label>
                <input name="email" value={company.email} onChange={handleCompanyChange} type="email" placeholder="contact@factory.com" className="w-full border border-amber-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-amber-400" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Phone Number *</label>
                <input name="phone" value={company.phone} onChange={handleCompanyChange} placeholder="+971 50 000 0000" className="w-full border border-amber-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-amber-400" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Number of Employees</label>
                <select name="employees" value={company.employees} onChange={handleCompanyChange} className="w-full border border-amber-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-amber-400 bg-white cursor-pointer">
                  <option value="">Select range...</option>
                  <option>1–10</option><option>11–50</option><option>51–200</option>
                  <option>201–500</option><option>501–1000</option><option>1000+</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Year Established</label>
                <input name="established" value={company.established} onChange={handleCompanyChange} placeholder="e.g. 2010" className="w-full border border-amber-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-amber-400" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Company Description</label>
                <textarea name="description" value={company.description} onChange={handleCompanyChange} rows={3} maxLength={500} placeholder="Briefly describe your factory, main products, and capabilities..." className="w-full border border-amber-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-amber-400 resize-none" />
                <div className="text-right text-xs text-gray-400 mt-1">{company.description.length}/500</div>
              </div>
            </div>
            <div className="mt-8 flex justify-end">
              <button
                onClick={() => {
                  if (!company.name || !company.country || !company.contactName || !company.email || !company.phone) {
                    alert("Please fill in all required fields.");
                    return;
                  }
                  setStep(2);
                }}
                className="bg-amber-500 hover:bg-amber-600 text-white font-semibold px-8 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
              >
                Next: Products &amp; Pricing <i className="ri-arrow-right-line"></i>
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 2: Products & Pricing ── */}
        {step === 2 && (
          <div className="space-y-5">
            {/* Dropshipping toggle */}
            <div className="bg-white rounded-2xl p-6 border border-amber-100">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-truck-line text-amber-500"></i> Dropshipping Settings
              </h2>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="font-semibold text-gray-800 text-sm">Dropshipping Available</p>
                  <p className="text-gray-500 text-xs mt-0.5">Allow buyers to dropship your products without holding inventory</p>
                </div>
                <div
                  onClick={() => setDropshippingAvailable(!dropshippingAvailable)}
                  className={`w-12 h-6 rounded-full transition-colors cursor-pointer relative flex-shrink-0 ${dropshippingAvailable ? "bg-amber-500" : "bg-gray-200"}`}
                >
                  <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${dropshippingAvailable ? "left-7" : "left-1"}`}></div>
                </div>
              </div>
              {dropshippingAvailable && (
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Warehouse / Shipping Countries</label>
                  <input
                    value={warehouseCountries}
                    onChange={(e) => setWarehouseCountries(e.target.value)}
                    placeholder="e.g. UAE, Saudi Arabia, China, USA..."
                    className="w-full border border-amber-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-amber-400"
                  />
                </div>
              )}
            </div>

            {/* Product list */}
            <div className="bg-white rounded-2xl p-6 border border-amber-100">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <i className="ri-price-tag-3-line text-amber-500"></i> Products &amp; Pricing
                </h2>
                <button
                  onClick={addProduct}
                  className="flex items-center gap-1 text-amber-600 border border-amber-300 hover:bg-amber-50 text-sm font-semibold px-3 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-line"></i> Add Product
                </button>
              </div>

              <div className="space-y-6">
                {products.map((prod, idx) => (
                  <div key={prod.id} className="border border-amber-100 rounded-xl p-5 bg-amber-50 relative">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm font-bold text-amber-700">Product #{idx + 1}</span>
                      {products.length > 1 && (
                        <button onClick={() => removeProduct(prod.id)} className="text-red-400 hover:text-red-600 cursor-pointer w-6 h-6 flex items-center justify-center">
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Product Name *</label>
                        <input value={prod.name} onChange={(e) => handleProductChange(prod.id, "name", e.target.value)} placeholder="e.g. Industrial LED Panel 200W" className="w-full border border-amber-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-amber-400 bg-white" />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Category *</label>
                        <select value={prod.category} onChange={(e) => handleProductChange(prod.id, "category", e.target.value)} className="w-full border border-amber-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-amber-400 bg-white cursor-pointer">
                          <option value="">Select category...</option>
                          <option>Electronics</option><option>Textiles & Fabric</option><option>Machinery</option>
                          <option>Food & Beverage</option><option>Furniture</option><option>Auto Parts</option>
                          <option>Beauty & Care</option><option>Construction</option><option>Packaging</option>
                          <option>Chemicals</option><option>Clothing</option><option>Home Decor</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Min. Order Qty (MOQ) *</label>
                        <input value={prod.moq} onChange={(e) => handleProductChange(prod.id, "moq", e.target.value)} type="number" placeholder="e.g. 50" className="w-full border border-amber-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-amber-400 bg-white" />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Sample Price (USD/unit) *</label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                          <input value={prod.samplePrice} onChange={(e) => handleProductChange(prod.id, "samplePrice", e.target.value)} type="number" placeholder="e.g. 25" className="w-full border border-amber-200 rounded-lg pl-7 pr-4 py-2.5 text-sm focus:outline-none focus:border-amber-400 bg-white" />
                        </div>
                        <p className="text-xs text-gray-400 mt-1">Price for 1–{prod.moq || "MOQ"} units (sample order)</p>
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Bulk Price (USD/unit) *</label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                          <input value={prod.bulkPrice} onChange={(e) => handleProductChange(prod.id, "bulkPrice", e.target.value)} type="number" placeholder="e.g. 12" className="w-full border border-amber-200 rounded-lg pl-7 pr-4 py-2.5 text-sm focus:outline-none focus:border-amber-400 bg-white" />
                        </div>
                        <p className="text-xs text-gray-400 mt-1">Price for {prod.moq || "MOQ"}+ units (bulk order)</p>
                      </div>
                      <div className="md:col-span-2">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Product Description</label>
                        <textarea value={prod.description} onChange={(e) => handleProductChange(prod.id, "description", e.target.value)} rows={2} maxLength={300} placeholder="Key features, materials, specifications..." className="w-full border border-amber-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-amber-400 bg-white resize-none" />
                      </div>
                      <div className="md:col-span-2">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <div
                            onClick={() => handleProductChange(prod.id, "dropshipping", !prod.dropshipping)}
                            className={`w-10 h-5 rounded-full transition-colors cursor-pointer relative flex-shrink-0 ${prod.dropshipping ? "bg-emerald-500" : "bg-gray-200"}`}
                          >
                            <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all ${prod.dropshipping ? "left-5" : "left-0.5"}`}></div>
                          </div>
                          <div>
                            <span className="text-sm font-semibold text-gray-800">Dropshipping Available for this Product</span>
                            {prod.dropshipping && <span className="ml-2 bg-emerald-100 text-emerald-700 text-xs px-2 py-0.5 rounded-full font-semibold">Dropship Badge will show</span>}
                          </div>
                        </label>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <button
                onClick={addProduct}
                className="mt-4 w-full border-2 border-dashed border-amber-300 text-amber-600 hover:bg-amber-50 text-sm font-semibold py-3 rounded-xl transition-colors cursor-pointer flex items-center justify-center gap-2"
              >
                <i className="ri-add-circle-line text-lg"></i> Add Another Product
              </button>
            </div>

            <div className="flex justify-between">
              <button onClick={() => setStep(1)} className="border border-amber-300 text-amber-600 hover:bg-amber-50 font-semibold px-6 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2">
                <i className="ri-arrow-left-line"></i> Back
              </button>
              <button onClick={() => setStep(3)} className="bg-amber-500 hover:bg-amber-600 text-white font-semibold px-8 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2">
                Next: Verification <i className="ri-arrow-right-line"></i>
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 3: Verification ── */}
        {step === 3 && (
          <div className="bg-white rounded-2xl p-6 md:p-8 border border-amber-100">
            <h2 className="text-xl font-bold text-gray-900 mb-2 flex items-center gap-2">
              <i className="ri-shield-check-line text-amber-500"></i> Verification &amp; Certifications
            </h2>
            <p className="text-gray-500 text-sm mb-6">Select all certifications your factory holds. Verified suppliers get a gold badge and higher visibility.</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              {[
                { key: "businessLicense", label: "Business License / Trade Registration", icon: "ri-file-text-line" },
                { key: "iso", label: "ISO 9001 / ISO 14001 Certified", icon: "ri-award-line" },
                { key: "ce", label: "CE Certified (European Standard)", icon: "ri-verified-badge-line" },
                { key: "halal", label: "Halal Certified", icon: "ri-leaf-line" },
              ].map((cert) => (
                <label key={cert.key} className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${verification[cert.key as keyof typeof verification] ? "border-amber-400 bg-amber-50" : "border-gray-100 hover:border-amber-200"}`}>
                  <input
                    type="checkbox"
                    checked={!!verification[cert.key as keyof typeof verification]}
                    onChange={(e) => setVerification((prev) => ({ ...prev, [cert.key]: e.target.checked }))}
                    className="w-4 h-4 accent-amber-500"
                  />
                  <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-lg flex-shrink-0">
                    <i className={`${cert.icon} text-amber-600`}></i>
                  </div>
                  <span className="text-sm font-medium text-gray-800">{cert.label}</span>
                </label>
              ))}
            </div>

            <div className="mb-6">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Other Certifications</label>
              <input
                value={verification.other}
                onChange={(e) => setVerification((prev) => ({ ...prev, other: e.target.value }))}
                placeholder="e.g. FDA, REACH, RoHS, SGS..."
                className="w-full border border-amber-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-amber-400"
              />
            </div>

            {/* Summary */}
            <div className="bg-amber-50 rounded-xl p-5 mb-6 border border-amber-100">
              <h3 className="font-bold text-gray-900 text-sm mb-3">Registration Summary</h3>
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex justify-between"><span>Company:</span><span className="font-semibold text-gray-900">{company.name || "—"}</span></div>
                <div className="flex justify-between"><span>Country:</span><span className="font-semibold text-gray-900">{company.country || "—"}</span></div>
                <div className="flex justify-between"><span>Products listed:</span><span className="font-semibold text-gray-900">{products.filter((p) => p.name).length}</span></div>
                <div className="flex justify-between"><span>Dropshipping:</span><span className={`font-semibold ${dropshippingAvailable ? "text-emerald-600" : "text-gray-500"}`}>{dropshippingAvailable ? "Available" : "Not available"}</span></div>
                <div className="flex justify-between"><span>Dropship products:</span><span className="font-semibold text-gray-900">{products.filter((p) => p.dropshipping).length}</span></div>
              </div>
            </div>

            <label className="flex items-start gap-3 cursor-pointer mb-6">
              <input
                type="checkbox"
                checked={verification.agreeTerms}
                onChange={(e) => setVerification((prev) => ({ ...prev, agreeTerms: e.target.checked }))}
                className="w-4 h-4 accent-amber-500 mt-0.5"
              />
              <span className="text-sm text-gray-600">
                I confirm that all information provided is accurate and I agree to the{" "}
                <a href="#" className="text-amber-600 underline">Terms of Service</a> and{" "}
                <a href="#" className="text-amber-600 underline">Supplier Policy</a>.
              </span>
            </label>

            <div className="flex justify-between">
              <button onClick={() => setStep(2)} className="border border-amber-300 text-amber-600 hover:bg-amber-50 font-semibold px-6 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2">
                <i className="ri-arrow-left-line"></i> Back
              </button>
              <button
                onClick={handleSubmit}
                disabled={!verification.agreeTerms || submitting}
                className="bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-bold px-8 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
              >
                {submitting ? "Submitting..." : <><i className="ri-send-plane-line"></i> Submit Registration</>}
              </button>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default RegisterSupplierPage;
